import { Fragment, ReactNode, useEffect, useState } from 'react';
import {
  PrismBanner,
  PrismButton,
  PrismDialog,
  PrismInputText,
  PrismLoading,
} from '@prism-ui/react';
import { DateTime } from 'luxon';
import useFetch from '../../../../hooks/useFetch';
import { RetentionSchedule, RetentionScheduleControls } from './RetentionScheduleControls';

interface RetentionSettings extends RetentionSchedule {
  id: number;
  monthsToRetain: number;
}

export interface RetentionRunResult {
  purgedCount: number;
  dependentRowsDeleted?: number;
  renamedLogoCount?: number;
  assetCleanupFailureCount?: number;
  pendingAssetCleanupCount?: number;
  cutoffDate: string;
}

interface RetentionPolicyProps {
  endpoint: string;
  subject: string;
  warning: (months: number, cutoff: string) => string;
  confirmation: (cutoff: string) => ReactNode;
  minimumMonths: number;
  runButtonLabel: string;
  formatRunSuccess: (result: RetentionRunResult) => string;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
  active?: boolean;
}

async function requestJson<T>(url: string, options: RequestInit): Promise<T> {
  const response = await fetch(url, options);
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.error || body.message || response.statusText);
  }
  return body as T;
}

export function RetentionPolicy({
  endpoint,
  subject,
  warning,
  confirmation,
  minimumMonths,
  runButtonLabel,
  formatRunSuccess,
  setSuccessMessage,
  setErrorMessage,
  active = true,
}: RetentionPolicyProps) {
  const { data: settings, error, loading, refresh } = useFetch<RetentionSettings>(endpoint);
  const [editing, setEditing] = useState(false);
  const [draftMonths, setDraftMonths] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (settings) setDraftMonths(String(settings.monthsToRetain));
  }, [settings]);

  const parsedMonths = Number(draftMonths);
  const isValid = draftMonths.trim() !== ''
    && Number.isInteger(parsedMonths)
    && parsedMonths >= minimumMonths;
  const displayedMonths = editing && isValid ? parsedMonths : settings?.monthsToRetain;
  const cutoff = displayedMonths === undefined
    ? ''
    : DateTime.now().minus({ months: displayedMonths }).toLocaleString(DateTime.DATE_FULL);

  const cancelEditing = () => {
    setDraftMonths(settings ? String(settings.monthsToRetain) : '');
    setEditing(false);
  };

  const startEditing = () => {
    // Always re-derive the draft from the true saved value when entering edit mode.
    // This guards against any stray/late-firing change event (e.g. from the input's
    // internal blur handling during the previous unmount) leaving a stale draft behind.
    setDraftMonths(settings ? String(settings.monthsToRetain) : '');
    setEditing(true);
  };

  // Escape-to-cancel is handled globally (rather than via onKeyDown on a specific
  // element) so it works regardless of which element currently has DOM focus -
  // autofocus into the freshly-mounted input isn't guaranteed to have landed yet,
  // and a scoped listener would silently do nothing if focus was still elsewhere.
  // Inactive (hidden) tabs stay mounted, so the listener is gated on `active` to
  // stop a background tab from cancelling its own edit on a keystroke meant for
  // the visible one.
  useEffect(() => {
    if (!editing || !active) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelEditing();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, active, settings]);

  const save = async () => {
    if (!isValid) return;
    setSubmitting(true);
    try {
      const result = await requestJson<RetentionSettings & { message: string }>(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthsToRetain: parsedMonths }),
      });
      setDraftMonths(String(result.monthsToRetain));
      setEditing(false);
      setSuccessMessage(`Successfully updated ${subject.toLowerCase()} retention settings.`);
      refresh();
    } catch (requestError) {
      setErrorMessage(`Error updating ${subject.toLowerCase()} retention settings: ${(requestError as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const saveSchedule = async (patch: {
    scheduleEnabled: boolean;
    scheduleHour: number;
    scheduleMinute: number;
    scheduleDayOfMonth: number;
  }) => {
    try {
      await requestJson<RetentionSettings>(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      setSuccessMessage(
        patch.scheduleEnabled
          ? `Scheduled ${subject.toLowerCase()} retention is now on.`
          : `Scheduled ${subject.toLowerCase()} retention is now off.`,
      );
      refresh();
    } catch (requestError) {
      setErrorMessage(`Error updating ${subject.toLowerCase()} retention schedule: ${(requestError as Error).message}`);
    }
  };

  const run = async () => {
    setSubmitting(true);
    try {
      const result = await requestJson<RetentionRunResult>(`${endpoint}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      setSuccessMessage(formatRunSuccess(result));
      setShowConfirmation(false);
      refresh();
    } catch (requestError) {
      setErrorMessage(`Error running ${subject.toLowerCase()} retention: ${(requestError as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !settings) {
    return <PrismLoading as="spinner">Loading retention settings</PrismLoading>;
  }

  if (error || !settings) {
    return (
      <PrismBanner tone="danger" variant="notification">
        Unable to load {subject.toLowerCase()} retention settings.
      </PrismBanner>
    );
  }

  return (
    <div className="prism-box clr:flex clr:flex-col clr:gap-md clr:w-full clr:bg-surface-primary-base clr:text-primary-base">
      <PrismBanner tone="danger" variant="notification">
        <strong>{warning(displayedMonths as number, cutoff)}</strong>
      </PrismBanner>

      <div className="clr:flex clr:flex-col clr:gap-sm">
        <strong>Number of months to retain {subject.toLowerCase()} data</strong>
        {editing ? (
          <PrismInputText
            key="editing-input"
            name={`${subject.toLowerCase().replace(/\s+/g, '-')}-retention-months`}
            label="Months to retain"
            required
            type="number"
            min={minimumMonths}
            step="1"
            value={draftMonths}
            invalid={!isValid}
            invalidMessage={`Enter a whole number of at least ${minimumMonths}.`}
            setAutofocus
            onPrismInput={(event: CustomEvent<{ value?: string | number }>) => {
              setDraftMonths(String(event.detail?.value ?? ''));
            }}
            onPrismChange={(event: CustomEvent<{ value?: string | number }>) => {
              setDraftMonths(String(event.detail?.value ?? ''));
            }}
          />
        ) : (
          <span key="viewing-value">Every {settings.monthsToRetain} months</span>
        )}
      </div>

      <div className="clr:flex clr:flex-wrap clr:justify-center clr:gap-sm">
        {editing ? (
          <Fragment key="editing-buttons">
            <PrismButton
              key="cancel-button"
              tone="secondary"
              display="outline"
              disabled={submitting}
              onPrismClick={cancelEditing}
            >
              Cancel
            </PrismButton>
            <PrismButton
              key="save-button"
              tone="success"
              disabled={submitting || !isValid || parsedMonths === settings.monthsToRetain}
              loading={submitting}
              onPrismClick={save}
            >
              Save
            </PrismButton>
          </Fragment>
        ) : (
          <Fragment key="viewing-buttons">
            <PrismButton
              key="edit-button"
              tone="secondary"
              display="outline"
              disabled={submitting}
              onPrismClick={startEditing}
            >
              Edit retention period
            </PrismButton>
            <PrismButton
              key="run-button"
              tone="danger"
              disabled={submitting}
              onPrismClick={() => setShowConfirmation(true)}
            >
              {runButtonLabel}
            </PrismButton>
          </Fragment>
        )}
      </div>

      <RetentionScheduleControls
        subject={subject}
        schedule={settings}
        disabled={submitting || editing}
        onSave={saveSchedule}
      />

      <PrismDialog
        open={showConfirmation}
        close-method="user"
        setAriaLabel={`Confirm ${subject.toLowerCase()} retention`}
        onPrismClose={() => {
          setShowConfirmation(false);
          setSubmitting(false);
        }}
      >
        <h3>Are you sure?</h3>
        {confirmation(cutoff)}
        <PrismButton
          slot="footer"
          tone="secondary"
          display="outline"
          disabled={submitting}
          onPrismClick={() => setShowConfirmation(false)}
        >
          Cancel
        </PrismButton>
        <PrismButton
          slot="footer"
          tone="danger"
          loading={submitting}
          disabled={submitting}
          onPrismClick={run}
        >
          Confirm
        </PrismButton>
      </PrismDialog>
    </div>
  );
}
